import React from 'react'
import PropTypes from 'prop-types'
import { Link, useRouteMatch } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { HyperButton, formatSalary } from '@offerzen/pinata-shame'

import DescriptionList, { DescriptionItem } from '@/Jobs/components/DescriptionList'
import RequirementTag from '@/Jobs/components/RequirementTag'
import { workplacePolicyTypeMap, remunerationPeriodMap } from '@/Jobs/helpers/constants'

import { buildJobDetailsHref } from '../helpers'

import styles from './styles'

const CardBody = ({
  roleExperience,
  otherRoles,
  mustHaveSkillExperiences,
  niceToHaveSkills,
  workplacePolicy,
  publishedSalaryRange,
  currency,
  remunerationPeriod,
  jobId,
  onViewDetailsClick,
}) => {
  const { url } = useRouteMatch()

  return (
    <div className={styles.cardBody}>
      <DescriptionList>
        <DescriptionItem term="Role" details={roleExperience?.role?.name} />

        <If condition={otherRoles?.length}>
          <DescriptionItem
            term="Other roles we'll consider"
            details={
              <ul>
                <For each="role" of={otherRoles} index="index">
                  <Choose>
                    <When condition={index === otherRoles.length - 1}>
                      <li key={role.id}>{role.name}</li>
                    </When>
                    <Otherwise>
                      <li key={role.id}>{role.name},</li>
                    </Otherwise>
                  </Choose>
                </For>
              </ul>
            }
          />
        </If>

        <DescriptionItem
          term="Experience in role"
          details={
            <Choose>
              <When condition={roleExperience?.yearsExperience === 0}>No experience required</When>
              <When condition={roleExperience?.yearsExperience}>{roleExperience.yearsExperience}+ years</When>
              <Otherwise />
            </Choose>
          }
        />

        <DescriptionItem
          term="Must-have skills"
          details={
            <If condition={mustHaveSkillExperiences?.length}>
              <ul>
                <For each="skillExperience" of={mustHaveSkillExperiences}>
                  <li key={skillExperience.id}>
                    <RequirementTag
                      text={`${skillExperience.skill.name}${
                        skillExperience?.yearsExperience ? ` (${skillExperience.yearsExperience} years exp.)` : ''
                      }`}
                    />
                  </li>
                </For>
              </ul>
            </If>
          }
        />

        <If condition={niceToHaveSkills?.length}>
          <DescriptionItem
            term="Nice-to-have skills"
            details={
              <ul>
                <For each="skillExperience" of={niceToHaveSkills}>
                  <li key={skillExperience.id}>
                    <RequirementTag text={skillExperience.name} />
                  </li>
                </For>
              </ul>
            }
          />
        </If>

        <DescriptionItem
          term="Location of position"
          details={
            <>
              <If condition={workplacePolicy?.type}>{workplacePolicyTypeMap[workplacePolicy.type]} from</If>
              <Choose>
                <When condition={workplacePolicy?.type === 'REMOTE' && !workplacePolicy?.comprehensiveLocations?.length}>
                  <ul>
                    <li>
                      <RequirementTag text="Anywhere" />
                    </li>
                  </ul>
                </When>
                <Otherwise>
                  <If condition={workplacePolicy?.comprehensiveLocations?.length}>
                    <ul>
                      <For each="workLocation" of={workplacePolicy.comprehensiveLocations}>
                        <li key={workLocation.displayAddress}>
                          <RequirementTag text={workLocation.displayAddress} />
                        </li>
                      </For>
                    </ul>
                  </If>
                </Otherwise>
              </Choose>
            </>
          }
        />

        <If condition={publishedSalaryRange && !isEmpty(publishedSalaryRange) && currency?.code}>
          <DescriptionItem
            term="Salary range"
            details={
              <>
                <Choose>
                  <When condition={publishedSalaryRange.start && publishedSalaryRange.end}>
                    {formatSalary(currency.code, publishedSalaryRange.start)} - {formatSalary(currency.code, publishedSalaryRange.end)}
                  </When>
                  <When condition={publishedSalaryRange.start}>From {formatSalary(currency.code, publishedSalaryRange.start)}</When>
                  <When condition={publishedSalaryRange.end}>Up to {formatSalary(currency.code, publishedSalaryRange.end)}</When>
                </Choose>{' '}
                {remunerationPeriodMap[remunerationPeriod]}
              </>
            }
          />
        </If>
      </DescriptionList>

      <HyperButton label="View details" onClick={onViewDetailsClick} href={buildJobDetailsHref(jobId, url)} as={Link} variant="outlined" />
    </div>
  )
}

CardBody.propTypes = {
  onViewDetailsClick: PropTypes.string,
  jobId: PropTypes.string,
  workplacePolicy: PropTypes.shape({
    type: PropTypes.oneOf(['HYBRID', 'REMOTE', 'OFFICE']),
    comprehensiveLocations: PropTypes.arrayOf(
      PropTypes.shape({
        displayAddress: PropTypes.string,
      }),
    ),
  }),
  publishedSalaryRange: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  currency: PropTypes.shape({
    code: PropTypes.string,
  }),
  remunerationPeriod: PropTypes.oneOf(['MONTH', 'YEAR']),
  roleExperience: PropTypes.shape({
    role: PropTypes.shape({
      name: PropTypes.string,
    }),
    yearsExperience: PropTypes.number,
  }),
  otherRoles: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
  mustHaveSkillExperiences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      skill: PropTypes.shape({ name: PropTypes.string }),
      yearsExperience: PropTypes.number,
    }),
  ),
  niceToHaveSkills: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
}

export default CardBody
